import { Component, computed, inject, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogConfig,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { uniqBy } from 'lodash';
import { distinctUntilChanged, filter, startWith, switchMap, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { EDialogWidth } from '../../../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../../../core/validators/trimmed-required.validator';
import { DatatableComponent } from '../../../../../shared/components/datatable/datatable.component';
import { IDatatableColumn } from '../../../../../shared/components/datatable/datatable.interface';
import { ErrorMessagesComponent } from '../../../../../shared/components/error-messages/error-messages.component';
import { InfoMessageComponent } from '../../../../../shared/components/info-message/info-message.component';
import { IDropdownOption, MatDropdown } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../../../shared/components/mat-dropdown/scw-mat-dropdown.interface';
import { EndpointResponseService } from '../../../../../shared/services/endpoint-response.service';
import { ELoadStatus } from '../../../../../state/state.interface';
import { FormUtilitiesService } from '../../../../../utilities/form-utilities.service';
import { ESnackbar, SnackbarService } from '../../../../../utilities/snackbar.service';
import { EWidgetRequestResultType } from '../../../data-integrations/widget-requests/widget-requests-add-edit-dialog/widget-requests-add-edit-dialog.interface';
import {
  ECustomFilterType,
  ICustomFilterAddEditDialogData,
  ICustomFilterAddPayload,
  IPredefinedCustomFilterValue,
} from '../custom-filters.interface';
import { CustomFiltersAddEditDialogStore } from './custom-filters-add-edit-dialog.store';

export interface IPredefinedCustomRequestMap {
  dropdownId: string | null;
  dropdownName: string;
}

export interface IPredefinedCustomRequestMapRow {
  id: string;
  dropdownId: FormControl<string>;
  dropdownName: FormControl<string>;
}

@Component({
  imports: [
    ErrorMessagesComponent,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    TranslatePipe,
    MatDropdown,
    MatIcon,
    DatatableComponent,
    MatMiniFabButton,
    MatTooltip,
    InfoMessageComponent,
  ],
  providers: [CustomFiltersAddEditDialogStore],
  selector: 'app-custom-filters-add-edit-dialog',
  styleUrl: './custom-filters-add-edit-dialog.component.scss',
  templateUrl: './custom-filters-add-edit-dialog.component.html',
})
export class CustomFiltersAddEditDialogComponent {
  public static dialogConfig: MatDialogConfig = {
    closeOnNavigation: true,
    disableClose: true,
    exitAnimationDuration: 0,
    maxWidth: '100%',
    restoreFocus: false,
    width: EDialogWidth.medium,
  };

  public readonly injectData = inject<ICustomFilterAddEditDialogData>(MAT_DIALOG_DATA);
  private readonly matDialogRef = inject(MatDialogRef);
  private readonly translate = inject(TranslateService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly endpointResponseService = inject(EndpointResponseService);
  public readonly store = inject(CustomFiltersAddEditDialogStore);

  public readonly addEditForm = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)],
    }),
    type: new FormControl<IDropdownOption<ECustomFilterType>[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  public readonly apiForm = new FormGroup({
    dropdownIdPath: new FormControl<IDropdownOption<string>[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    dropdownNamePath: new FormControl<IDropdownOption<string>[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    widgetRequestId: new FormControl<IDropdownOption[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  public readonly pathOptionsStatus = signal(ELoadStatus.initial);

  // Raw response of the selected endpoint — feeds both the property dropdowns and the JSON preview.
  public readonly endpointResponse = toSignal(
    this.apiForm.controls.widgetRequestId.valueChanges.pipe(
      startWith(this.apiForm.controls.widgetRequestId.value),
      filter((value) => Boolean(value.length)),
      map(([{ id }]) => id),
      distinctUntilChanged(),
      tap(() => {
        this.apiForm.controls.dropdownIdPath.reset([]);
        this.apiForm.controls.dropdownNamePath.reset([]);
        this.pathOptionsStatus.set(ELoadStatus.loading);
      }),
      switchMap((widgetRequestId) => this.endpointResponseService.fetchResponse(widgetRequestId, this.pathOptionsStatus)),
    ),
    { initialValue: null as unknown },
  );

  // First item of the response — the sample we extract property paths and example values from.
  private readonly endpointSampleItem = computed(() => this.endpointResponseService.getSampleItem(this.endpointResponse()));

  public readonly pathOptionsOfSelectedResource = computed(() =>
    this.endpointResponseService.buildPathOptions(this.endpointSampleItem()),
  );

  private readonly selectedValueKeyOption = toSignal(this.apiForm.controls.dropdownIdPath.valueChanges, {
    initialValue: this.apiForm.controls.dropdownIdPath.value,
  });

  private readonly selectedLabelKeyOption = toSignal(this.apiForm.controls.dropdownNamePath.valueChanges, {
    initialValue: this.apiForm.controls.dropdownNamePath.value,
  });

  public readonly typeDropdownSettings: IDropdownSettings = {
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.type'),
  };

  public readonly typeDropdownOptions: IDropdownOption<ECustomFilterType>[] = Object.values(ECustomFilterType).map(
    (type) => ({
      id: type,
      name: this.translate.instant(`field.${type}`),
    }),
  );

  public readonly widgetRequestDropdownSettings: IDropdownSettings = {
    enableSearchFilter: true,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.endpoint'),
  };

  // Note: no `hasDelayedDefaultValue` — these dropdowns store the real path as both id and name (never
  // the '...' placeholder), so they don't need delayed resolution. Keeping it off also prevents
  // mat-dropdown's render effect from calling setValue, which would loop against the value-dependent hint.
  public readonly valueKeyDropdownSettings = computed<IDropdownSettings>(() => ({
    enableSearchFilter: true,
    hint: this.endpointResponseService.buildExampleHint(this.endpointSampleItem(), this.selectedValueKeyOption()[0]?.id),
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.valueKey'),
  }));

  public readonly labelKeyDropdownSettings = computed<IDropdownSettings>(() => ({
    enableSearchFilter: true,
    hint: this.endpointResponseService.buildExampleHint(this.endpointSampleItem(), this.selectedLabelKeyOption()[0]?.id),
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.labelKey'),
  }));

  public readonly predefinedCustomFilterMapRowOrders: WritableSignal<string[]> = signal<string[]>([]);
  public readonly predefinedCustomFilterMapRows: WritableSignal<IPredefinedCustomRequestMapRow[]> = signal<
    IPredefinedCustomRequestMapRow[]
  >([]);

  public readonly orderedPredefinedCustomFilterMapRows = computed(() => {
    return this.predefinedCustomFilterMapRows()
      .map((fieldOption: IPredefinedCustomRequestMapRow) => ({ ...fieldOption, delete: false }))
      .sort(
        (a, b) =>
          this.predefinedCustomFilterMapRowOrders().indexOf(a.id) -
          this.predefinedCustomFilterMapRowOrders().indexOf(b.id),
      );
  });

  public readonly widgetRequestMapDatatableColumns: IDatatableColumn<
    IPredefinedCustomRequestMapRow & {
      delete: boolean;
    },
    string
  >[] = [
    { id: 'id', isSortable: false },
    { id: 'dropdownId' },
    { id: 'dropdownName' },
    { id: 'delete', isSortable: false },
  ];

  public readonly ELoadStatus = ELoadStatus;
  public readonly ECustomFilterType = ECustomFilterType;

  constructor() {
    const { editData } = this.injectData;

    if (editData) {
      this.addEditForm.reset({
        name: editData.name ?? '',
        type: [{ id: editData.typeBasedConfiguration.type, name: '...' }],
      });
      this.predefinedCustomFilterMapRows.set([]);
      this.predefinedCustomFilterMapRowOrders.set([]);

      if (editData.typeBasedConfiguration.type === ECustomFilterType.manual) {
        for (const { id, name } of editData.typeBasedConfiguration.values) {
          this.addPredefinedOption({ dropdownId: id, dropdownName: name });
        }
      } else if (editData.typeBasedConfiguration.type === ECustomFilterType.external) {
        const { dropdownIdPath, dropdownNamePath, widgetRequestId } = editData.typeBasedConfiguration;

        // Set the endpoint first: this triggers the path-options fetch and resets the path
        // selections, so the id/name paths must be restored afterwards.
        this.apiForm.controls.widgetRequestId.setValue([{ id: widgetRequestId, name: '...' }]);
        this.apiForm.controls.dropdownIdPath.setValue(dropdownIdPath ? [{ id: dropdownIdPath, name: dropdownIdPath }] : []);
        this.apiForm.controls.dropdownNamePath.setValue([{ id: dropdownNamePath, name: dropdownNamePath }]);
      }

      this.store.loadInUseData(editData.id);
    }

    this.store.loadWidgetRequests({
      filters: [{ field: 'resultType', ids: [EWidgetRequestResultType.multipleValues] }],
      limit: 1000,
    });

    this.store.afterCreateUpdateSuccess$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
      this.matDialogRef.close();
    });

    this.addEditForm.controls.type.valueChanges
      .pipe(
        filter((value) => value?.[0].id === ECustomFilterType.manual),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        if (!this.predefinedCustomFilterMapRows().length) {
          this.addPredefinedOption();
        }
      });
  }

  public onFormDialogSubmit(): void {
    for (const { dropdownId, dropdownName } of this.orderedPredefinedCustomFilterMapRows()) {
      dropdownId.markAsTouched();
      dropdownName.markAsTouched();
    }

    const type = this.addEditForm.controls.type.value?.[0].id;

    if (
      !this.addEditForm.valid ||
      (type === ECustomFilterType.manual && !this.formatPredefinedMapRowsForOutput().length) ||
      (type === ECustomFilterType.external &&
        (!this.apiForm.valid || this.pathOptionsStatus() === ELoadStatus.failure))
    ) {
      return;
    }

    if (this.manualOptionsHasDuplicateIds()) {
      this.snackbarService.open(
        this.translate.instant('page.settings.customFilter.manualOptionsHasDuplicateIds'),
        ESnackbar.error,
      );

      return;
    }

    const payload = this.formatOnePayload();

    if (this.injectData.editData?.id) {
      this.store.updateOne({ dto: payload, id: this.injectData.editData.id });
    } else {
      this.store.createOne(payload);
    }
  }

  public addPredefinedOption(value?: IPredefinedCustomRequestMap): void {
    const id: string = crypto.randomUUID();
    this.predefinedCustomFilterMapRowOrders.update((dashboardOptionOrders: string[]) => [...dashboardOptionOrders, id]);

    const dropdownIdControl = new FormControl(value?.dropdownId ?? '', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(50)],
    });

    const dropdownNameControl = new FormControl(value?.dropdownName ?? '', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(50)],
    });

    this.predefinedCustomFilterMapRows.update((options) => [
      ...options,
      {
        dropdownId: dropdownIdControl,
        dropdownName: dropdownNameControl,
        id,
      },
    ]);
  }

  public openEndpointPreview(): void {
    this.endpointResponseService.openPreview(this.endpointResponse());
  }

  public deleteMapRow(rowId: string): void {
    this.predefinedCustomFilterMapRowOrders.set(
      this.predefinedCustomFilterMapRowOrders().filter((id: string) => id !== rowId),
    );
    this.predefinedCustomFilterMapRows.set(this.predefinedCustomFilterMapRows().filter((row) => row.id !== rowId));
  }

  private formatOnePayload(): ICustomFilterAddPayload {
    const { value } = this.addEditForm;
    const type = value.type![0].id;
    const apiFormValues = this.apiForm.value;

    return {
      name: FormUtilitiesService.trimIfString(value.name!),
      typeBasedConfiguration:
        type === ECustomFilterType.manual
          ? { type, values: this.formatPredefinedMapRowsForOutput() }
          : {
              dropdownIdPath: apiFormValues.dropdownIdPath![0].id,
              dropdownNamePath: apiFormValues.dropdownNamePath![0].id,
              type,
              widgetRequestId: apiFormValues.widgetRequestId![0].id,
            },
    };
  }

  private formatPredefinedMapRowsForOutput(): IPredefinedCustomFilterValue[] {
    const widgetRequestMapRows = this.predefinedCustomFilterMapRows();

    if (widgetRequestMapRows.some(({ dropdownName, dropdownId }) => dropdownName.invalid || dropdownId.invalid)) {
      return [];
    }

    return widgetRequestMapRows
      .map((row) => ({
        dropdownId: row.dropdownId.value,
        dropdownName: row.dropdownName.value,
        id: row.id,
      }))
      .sort(
        (a, b) =>
          this.predefinedCustomFilterMapRowOrders().indexOf(a.id) -
          this.predefinedCustomFilterMapRowOrders().indexOf(b.id),
      )
      .map(({ dropdownId, dropdownName }) => ({ id: dropdownId ?? null, name: dropdownName }));
  }

  private manualOptionsHasDuplicateIds(): boolean {
    if (this.addEditForm.controls.type.value?.[0].id !== ECustomFilterType.manual) {
      return false;
    }

    const formatPredefinedMapRowsForOutput = this.formatPredefinedMapRowsForOutput();

    return (
      formatPredefinedMapRowsForOutput.length !== uniqBy(formatPredefinedMapRowsForOutput, 'id').length ||
      formatPredefinedMapRowsForOutput.length !== uniqBy(formatPredefinedMapRowsForOutput, 'name').length
    );
  }
}
